import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import axios from "axios";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const EditBannerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    start_time: "",
    end_time: "",
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [updatedBannerDetails, setUpdatedBannerDetails] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/admin/banners/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        const banner = response.data.banner;
        setFormData({
          title: banner.title,
          message: banner.message,
          start_time: banner.start_time.slice(0, 16),
          end_time: banner.end_time.slice(0, 16),
          is_active: !!banner.is_active,
        });
      } catch (error) {
        toast.error("Failed to load banner.");
        navigate("/banners");
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanner();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "title" && value.length > 100) {
      setErrors((prev) => ({ ...prev, title: "Title cannot exceed 100 characters" }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (formData.title.length > 100) newErrors.title = "Title cannot exceed 100 characters";
    if (!formData.message) newErrors.message = "Message is required";
    if (!formData.start_time) newErrors.start_time = "Start time is required";
    if (!formData.end_time) newErrors.end_time = "End time is required";
    if (formData.end_time && formData.start_time && new Date(formData.end_time) < new Date(formData.start_time)) {
      newErrors.end_time = "End time must be after start time";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const response = await axios.put(`${BASE_URL}/admin/banners/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      if (response.data.status === "success") {
        setUpdatedBannerDetails(response.data.banner);
        setShowSuccessModal(true);
      } else {
        toast.error(response.data.message || "Failed to update banner.");
      }
    } catch (error) {
      toast.error("Failed to update banner: " + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setShowSuccessModal(false);
    navigate("/banners");
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <ToastContainer />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-center mb-6">
          <button onClick={() => navigate("/banners")} className="mr-4 text-gray-600 hover:text-gray-800">
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Edit Banner Alert</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                maxLength={100}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                rows={4}
              />
              {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Time</label>
                <input
                  type="datetime-local"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                {errors.start_time && <p className="mt-1 text-sm text-red-600">{errors.start_time}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Time</label>
                <input
                  type="datetime-local"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                {errors.end_time && <p className="mt-1 text-sm text-red-600">{errors.end_time}</p>}
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-sm text-gray-700">Active</label>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/banners")}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:opacity-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin h-5 w-5 text-white" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Update Banner</span>
              )}
            </button>
          </div>
        </form>
      </motion.div>

      <AnimatePresence>
        {showSuccessModal && updatedBannerDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full mx-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Banner updated successfully</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-5 rounded-xl">
                  <p className="text-sm font-semibold text-green-800 mb-3">Banner Details</p>
                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">ID:</span> {updatedBannerDetails.id}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Title:</span> {updatedBannerDetails.title}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Message:</span> {updatedBannerDetails.message}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Start Time:</span> {new Date(updatedBannerDetails.start_time).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">End Time:</span> {new Date(updatedBannerDetails.end_time).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Active:</span> {updatedBannerDetails.is_active ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={closeModal}
                    className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Go to Banners List
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EditBannerForm;